import { DEFAULT_EXAM_CATEGORIES } from "@/lib/exam-categories/defaults"
import type { ExamCategory } from "@/lib/exam-categories/types"
import {
  findCategory,
  getCategoryPathLabel,
} from "@/lib/exam-categories/tree"
import { leadInPlainText } from "@/lib/lead-in-html"
import { sanitizeLeoCopy } from "@/lib/leo-openrouter"

import {
  DIFFICULTY_TAG_LABEL,
  normalizeBloomTag,
  normalizeDifficultyTag,
  suggestBloomLevel,
  suggestDifficultyTag,
  type SuggestedDifficulty,
} from "./suggest-tags"
import type {
  QuestionQualityAlternate,
  QuestionQualityCheck,
  QuestionQualitySnapshot,
} from "./types"

export const CLASSIFICATION_BLOOM_CHECK_ID = "classification-bloom"
export const CLASSIFICATION_DIFFICULTY_CHECK_ID = "classification-difficulty"
export const CLASSIFICATION_IDENTIFIED_CHECK_ID = "classification-identified"

export const HAND_HYGIENE_CLASSIFICATION_BLOOM = "Remember"
export const HAND_HYGIENE_CLASSIFICATION_DIFFICULTY = "easy"
export const HAND_HYGIENE_CLASSIFICATION_CATEGORY_ID =
  "infectious-disease-prevent-infection"
export const HAND_HYGIENE_CLASSIFICATION_CATEGORY_LABEL = "Prevent infection"

const HAND_HYGIENE_STEM_RE =
  /hand hygiene[\s\S]*soap and water[\s\S]*seconds/i

export function isHandHygieneClassificationStem(stem: string): boolean {
  return HAND_HYGIENE_STEM_RE.test(leadInPlainText(stem))
}

export type ClassificationAsk = {
  bloom: boolean
  difficulty: boolean
  bloomLevel?: string
  difficultyLevel?: SuggestedDifficulty
}

const BLOOM_LEVELS = [
  "Remember",
  "Understand",
  "Apply",
  "Analyze",
  "Evaluate",
  "Create",
] as const

function requestedBloomLevel(text: string): string | undefined {
  const lower = text.toLowerCase()
  const asksForBloom =
    /bloom/.test(lower) ||
    /taxonomy/.test(lower) ||
    /thinking level/.test(lower) ||
    /cognitive level/.test(lower)
  if (!asksForBloom) return undefined
  return BLOOM_LEVELS.find((level) => new RegExp(`\\b${level}\\b`, "i").test(text))
}

function requestedDifficultyLevel(text: string): SuggestedDifficulty | undefined {
  if (/\b(high|harder|hard)\b/.test(text)) return "hard"
  if (/\b(low|easier|easy)\b/.test(text)) return "easy"
  if (/\b(normal|medium)\b/.test(text)) return "medium"
  return undefined
}

export function parseClassificationAsk(prompt: string): ClassificationAsk | null {
  const text = prompt.trim().toLowerCase()
  if (!text) return null
  const bloomLevel = requestedBloomLevel(prompt)
  const difficultyLevel = requestedDifficultyLevel(text)
  const bloom = /bloom/.test(text) || Boolean(bloomLevel)
  const difficulty = /difficult/.test(text) || Boolean(difficultyLevel && /set |change |make it /.test(text) && /difficult|level/.test(text))
  if (!bloom && !difficulty) {
    if (
      /suggest (the )?tags\b/.test(text) ||
      /tag this (item|question)/.test(text) ||
      /classif/.test(text) ||
      /categor/.test(text)
    ) {
      return { bloom: true, difficulty: true }
    }
    return null
  }
  return {
    bloom: bloom || !difficulty,
    difficulty: difficulty || !bloom,
    bloomLevel,
    difficultyLevel,
  }
}

function bloomAlternate(level: string): QuestionQualityAlternate {
  return {
    id: `classification-bloom-${level.toLowerCase()}`,
    text: level,
    actionKind: "set-bloom",
    actionLabel: "Use this Bloom’s",
    patch: { bloom: level },
    parameterDeltas: [],
    clearsCheckIds: [CLASSIFICATION_BLOOM_CHECK_ID],
  }
}

function difficultyAlternate(tag: SuggestedDifficulty): QuestionQualityAlternate {
  return {
    id: `classification-difficulty-${tag}`,
    text: DIFFICULTY_TAG_LABEL[tag],
    actionKind: "set-difficulty",
    actionLabel: "Use this difficulty",
    patch: { difficulty: tag },
    parameterDeltas: [],
    clearsCheckIds: [CLASSIFICATION_DIFFICULTY_CHECK_ID],
  }
}

function classificationIdentifiedAlternate(): QuestionQualityAlternate {
  return {
    id: "classification-identified-apply",
    text: `${HAND_HYGIENE_CLASSIFICATION_BLOOM}, ${HAND_HYGIENE_CLASSIFICATION_CATEGORY_LABEL}, ${DIFFICULTY_TAG_LABEL[HAND_HYGIENE_CLASSIFICATION_DIFFICULTY]}`,
    actionKind: "set-classification",
    actionLabel: "Apply",
    patch: {
      bloom: HAND_HYGIENE_CLASSIFICATION_BLOOM,
      difficulty: HAND_HYGIENE_CLASSIFICATION_DIFFICULTY,
      categoryIds: [HAND_HYGIENE_CLASSIFICATION_CATEGORY_ID],
    },
    parameterDeltas: [
      { parameter: "cognitive", effect: "clears" },
      { parameter: "difficulty", effect: "clears" },
    ],
    clearsCheckIds: [CLASSIFICATION_IDENTIFIED_CHECK_ID],
  }
}

/** Import starts without Bloom, category, or difficulty. One Apply writes all three. */
export function classificationIdentifiedChecks(
  snapshot: QuestionQualitySnapshot,
): QuestionQualityCheck[] {
  if (!isHandHygieneClassificationStem(snapshot.leadIn)) return []
  const bloomSet = Boolean(normalizeBloomTag(snapshot.bloom))
  const difficultySet = Boolean(normalizeDifficultyTag(snapshot.difficulty))
  const categorySet = (snapshot.categoryIds?.length ?? 0) > 0
  if (bloomSet && difficultySet && categorySet) return []

  const alternate = classificationIdentifiedAlternate()
  return [
    {
      id: CLASSIFICATION_IDENTIFIED_CHECK_ID,
      parameter: "cognitive",
      severity: "caution",
      field: "settings",
      settingsTarget: "classification",
      title: "Classification",
      mechanism:
        "Leo identified Bloom's taxonomy, category, and difficulty.",
      actionKind: "set-classification",
      actionLabel: "Apply",
      patch: alternate.patch,
      alternates: [alternate],
    },
  ]
}

export function classificationChecks(
  snapshot: QuestionQualitySnapshot,
  ask: ClassificationAsk,
  options?: { when?: "always" | "mismatch" },
): QuestionQualityCheck[] {
  const when = options?.when ?? "always"
  const checks: QuestionQualityCheck[] = []
  if (ask.bloom) {
    const bloom =
      ask.bloomLevel ||
      suggestBloomLevel(snapshot.leadIn) ||
      (!normalizeBloomTag(snapshot.bloom) ? "Remember" : null)
    if (
      bloom &&
      (when === "always" || normalizeBloomTag(snapshot.bloom) !== bloom)
    ) {
      const alternate = bloomAlternate(bloom)
      checks.push({
        id: CLASSIFICATION_BLOOM_CHECK_ID,
        parameter: "cognitive",
        severity: "caution",
        field: "settings",
        settingsTarget: "bloom",
        title: "Bloom’s",
        currentText: snapshot.bloom || undefined,
        mechanism: bloomReasonForStem(snapshot.leadIn, bloom),
        actionKind: "set-bloom",
        actionLabel: "Use this Bloom’s",
        patch: alternate.patch,
        alternates: [alternate],
      })
    }
  }
  if (ask.difficulty) {
    const difficulty =
      ask.difficultyLevel ||
      suggestDifficultyTag(snapshot.leadIn, snapshot.type) ||
      (!normalizeDifficultyTag(snapshot.difficulty) ? "easy" : null)
    if (
      difficulty &&
      (when === "always" ||
        normalizeDifficultyTag(snapshot.difficulty) !== difficulty)
    ) {
      const alternate = difficultyAlternate(difficulty)
      checks.push({
        id: CLASSIFICATION_DIFFICULTY_CHECK_ID,
        parameter: "difficulty",
        severity: "caution",
        field: "settings",
        settingsTarget: "difficulty",
        title: "Difficulty",
        currentText: (() => {
          const tag = normalizeDifficultyTag(snapshot.difficulty)
          return tag ? DIFFICULTY_TAG_LABEL[tag] : undefined
        })(),
        mechanism: difficultyReasonForStem(
          snapshot.leadIn,
          snapshot.type,
          difficulty,
        ),
        actionKind: "set-difficulty",
        actionLabel: "Use this difficulty",
        patch: alternate.patch,
        alternates: [alternate],
      })
    }
  }
  return checks
}

export const CLASSIFICATION_BLOOM_LEVELS = BLOOM_LEVELS

export const CLASSIFICATION_CATEGORY_CHECK_ID = "classification-category"

export type ClassificationField = "bloom" | "category" | "difficulty"

export type ClassificationCardModel = {
  fields?: ClassificationField[]
  bloom: string
  categoryId: string
  categoryLabel: string
  difficulty: SuggestedDifficulty | ""
  bloomReason: string
  categoryReason: string
  categoryCandidates?: Array<{ id: string; path: string; reason: string }>
  difficultyReason: string
  followUp: string
}

export type LeoClassificationPayload = {
  bloom?: string
  difficulty?: string
  categoryId?: string
  bloomReason?: string
  categoryReason?: string
  categoryCandidates?: Array<{ id: string; reason: string }>
  difficultyReason?: string
}

export function classificationCategoryChoices(categories: ExamCategory[] = DEFAULT_EXAM_CATEGORIES): Array<{
  id: string
  label: string
}> {
  return categories.filter((category) => category.isActive).map((category) => ({
    id: category.id,
    label: getCategoryPathLabel(categories, category.id),
  }))
}

function classificationReason(text: string | undefined, empty: string): string {
  const clean = sanitizeLeoCopy(text ?? "")
    .replace(/\bitems?\b/gi, "this question")
    .replace(/\s+/g, " ")
    .trim()
  return clean || empty
}

function categoryLabelFor(id: string, categories: ExamCategory[] = DEFAULT_EXAM_CATEGORIES): string {
  if (!id) return ""
  return findCategory(categories, id)?.name ?? ""
}

const CATEGORY_MATCH_STOP = new Set([
  "this",
  "that",
  "with",
  "from",
  "what",
  "when",
  "where",
  "which",
  "have",
  "been",
  "into",
  "about",
  "after",
  "before",
  "their",
  "there",
  "these",
  "those",
  "than",
  "then",
  "them",
  "they",
  "your",
  "must",
  "does",
  "each",
  "only",
])

function categoryMatchTokens(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 3 && !CATEGORY_MATCH_STOP.has(word))
}

/**
 * A catalog category when the model returns none.
 *
 * The tray otherwise shows an empty picker. The topic and the stem pick a
 * real leaf, so Apply still writes an id the catalog knows.
 */
export function fallbackCategorySuggestion(input: {
  stem: string
  topic?: string
  categories?: ExamCategory[]
}): { id: string; reason: string } | null {
  const categories = input.categories ?? DEFAULT_EXAM_CATEGORIES
  const stemWords = new Set(categoryMatchTokens(input.stem))
  const topicWords = new Set(categoryMatchTokens(input.topic ?? ""))
  const parents = categories.filter((category) => category.isActive && !category.parentId)
  const leaves = categories.filter((category) => category.isActive && category.parentId)
  let bestParent: ExamCategory | null = null
  let bestParentScore = 0
  for (const parent of parents) {
    const words = categoryMatchTokens(`${parent.name} ${parent.description ?? ""}`)
    let score = 0
    for (const word of words) {
      if (topicWords.has(word)) score += word.length * 3
      if (stemWords.has(word)) score += word.length
    }
    if (score > bestParentScore) {
      bestParent = parent
      bestParentScore = score
    }
  }
  const pool = bestParent
    ? leaves.filter((leaf) => leaf.parentId === bestParent.id)
    : leaves
  let best: ExamCategory | null = null
  let bestScore = -1
  for (const leaf of pool) {
    const words = categoryMatchTokens(`${leaf.name} ${leaf.description ?? ""}`)
    let score = 0
    for (const word of words) {
      if (stemWords.has(word)) score += word.length
      if (topicWords.has(word)) score += word.length
    }
    if (score > bestScore) {
      best = leaf
      bestScore = score
    }
  }
  const chosen = bestParentScore > 0 || bestScore > 0 ? best : null
  if (!chosen) return null
  const parent = parents.find((row) => row.id === chosen.parentId)
  return {
    id: chosen.id,
    reason: parent
      ? `${chosen.name} is the closest skill under ${parent.name} for this question.`
      : `${chosen.name} is the closest skill this question asks the student to use.`,
  }
}

/** Keeps a model category. Fills one from the catalog when the model named none. */
export function attachCategorySuggestion(
  suggested: LeoClassificationPayload | null,
  input: { stem: string; topic?: string; categories?: ExamCategory[] },
): LeoClassificationPayload | null {
  const hasCategory =
    Boolean(suggested?.categoryId) || (suggested?.categoryCandidates?.length ?? 0) > 0
  if (hasCategory && suggested) return suggested
  const fallback = fallbackCategorySuggestion(input)
  if (!fallback) return suggested
  return {
    bloom: suggested?.bloom,
    difficulty: suggested?.difficulty,
    bloomReason: suggested?.bloomReason,
    difficultyReason: suggested?.difficultyReason,
    categoryId: fallback.id,
    categoryReason: fallback.reason,
    categoryCandidates: [{ id: fallback.id, reason: fallback.reason }],
  }
}

export function parseLeoClassificationPayload(
  raw: unknown,
  categories: ExamCategory[] = DEFAULT_EXAM_CATEGORIES,
): LeoClassificationPayload | null {
  if (!raw || typeof raw !== "object") return null
  const row = raw as Record<string, unknown>
  const bloom = typeof row.bloom === "string" ? normalizeBloomTag(row.bloom) : ""
  const difficulty =
    typeof row.difficulty === "string"
      ? normalizeDifficultyTag(row.difficulty)
      : ""
  const categoryId =
    typeof row.categoryId === "string" &&
    findCategory(categories, row.categoryId)
      ? row.categoryId
      : ""
  const categoryCandidates = Array.isArray(row.categoryCandidates)
    ? row.categoryCandidates.flatMap((candidate) => {
        if (!candidate || typeof candidate !== "object") return []
        const value = candidate as Record<string, unknown>
        if (typeof value.id !== "string" || !findCategory(categories, value.id)) return []
        return [{ id: value.id, reason: typeof value.reason === "string" ? value.reason : "" }]
      }).filter((candidate, index, all) => all.findIndex((other) => other.id === candidate.id) === index).slice(0, 3)
    : []
  if (categoryId) categoryCandidates.sort((left, right) => Number(right.id === categoryId) - Number(left.id === categoryId))
  if (!bloom && !difficulty && !categoryId && categoryCandidates.length === 0) return null
  return {
    bloom: bloom || undefined,
    difficulty: difficulty || undefined,
    categoryId: categoryId || categoryCandidates[0]?.id,
    categoryCandidates,
    bloomReason:
      typeof row.bloomReason === "string" ? row.bloomReason : undefined,
    categoryReason:
      typeof row.categoryReason === "string" ? row.categoryReason : undefined,
    difficultyReason:
      typeof row.difficultyReason === "string"
        ? row.difficultyReason
        : undefined,
  }
}

function stemCue(
  stem: string,
  verbs: readonly string[],
): string | null {
  const text = leadInPlainText(stem).replace(/\s+/g, " ").trim()
  const match = text.match(
    new RegExp(`\\b(${verbs.join("|")})\\b\\s+([^?.]{0,70})`, "i"),
  )
  if (!match?.[1]) return null
  const rest = match[2]?.replace(/\s+/g, " ").trim() ?? ""
  return rest ? `${match[1].toLowerCase()} ${rest}` : match[1].toLowerCase()
}

export function bloomReasonForStem(stem: string, bloom: string): string {
  const text = leadInPlainText(stem).replace(/\s+/g, " ").trim()
  if (bloom === "Create") {
    const cue = stemCue(stem, [
      "design",
      "construct",
      "compose",
      "invent",
      "propose",
    ])
    if (cue) {
      return `Create fits because the student must ${cue}, which asks for a new product rather than a known response.`
    }
    return "Create fits because the student must produce a new product rather than select a known response."
  }
  if (bloom === "Evaluate") {
    const cue = stemCue(stem, [
      "critique",
      "justify",
      "evaluate",
      "assess",
      "argue",
      "judge",
    ])
    if (cue) {
      return `Evaluate fits because the student must ${cue}, which asks for a judgment.`
    }
    return "Evaluate fits because the student must judge a choice, not recall a fact."
  }
  if (bloom === "Analyze") {
    if (/\bexcept\b/i.test(text)) {
      return "Analyze fits because the student must find the statement that does not belong."
    }
    const cue = stemCue(stem, [
      "differentiate",
      "distinguish",
      "compare",
      "contrast",
      "analyze",
    ])
    if (cue) {
      return `Analyze fits because the student must ${cue}, which asks them to separate ideas.`
    }
    return "Analyze fits because the student must separate ideas, not recall one fact."
  }
  if (bloom === "Apply") {
    const cue = stemCue(stem, [
      "calculate",
      "compute",
      "solve",
      "demonstrate",
      "apply",
    ])
    if (cue) {
      return `Apply fits because the student must ${cue} in this situation.`
    }
    return "Apply fits because the student must use a known rule in this situation."
  }
  if (bloom === "Understand") {
    const cue = stemCue(stem, [
      "explain",
      "describe",
      "summarise",
      "summarize",
      "classify",
      "interpret",
    ])
    if (cue) {
      return `Understand fits because the student must ${cue}, which shows meaning rather than a new product.`
    }
    return "Understand fits because the student must show the meaning of a known idea."
  }
  if (/how long|last at least|duration|seconds|must last/i.test(text)) {
    return "Remember fits because the student recalls one duration, not a decision."
  }
  if (/\b(which|what is|name the|identify)\b/i.test(text)) {
    return "Remember fits because the student names a known fact from the stem."
  }
  return "Remember fits because the student recalls a single fact."
}

export function difficultyReasonForStem(
  stem: string,
  type: string,
  difficulty: SuggestedDifficulty,
): string {
  const text = leadInPlainText(stem).replace(/\s+/g, " ").trim()
  if (difficulty === "hard") {
    if (/\bexcept\b/i.test(text)) {
      return "High fits because the student must find the exception, not the expected fact."
    }
    if (/\b(calculate|compute|multi-step)\b/i.test(text)) {
      return "High fits because the student must complete more than one step."
    }
    return "High fits because the student must choose among close options."
  }
  if (difficulty === "medium") {
    return "Normal fits because the student uses a familiar case with one expected response."
  }
  if (type === "true_false") {
    return "Low fits because the student only decides true or false."
  }
  if (/last at least|duration|seconds|what is|name the|define/i.test(text)) {
    return "Low fits because the stem asks for one recalled duration or fact."
  }
  return "Low fits because the student recalls one fact."
}

function classificationFollowUp(model: {
  bloom: string
  categoryLabel: string
  difficulty: SuggestedDifficulty | ""
}): string {
  const parts = [
    model.bloom,
    model.categoryLabel,
    model.difficulty ? DIFFICULTY_TAG_LABEL[model.difficulty] : "",
  ].filter(Boolean)
  if (parts.length === 0) {
    return "Pick Bloom's, a category, and difficulty that match this stem."
  }
  if (parts.length === 1) {
    return `Would ${parts[0]} work for this question?`
  }
  return `Would ${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]} work for this question?`
}

export function classificationFieldApplyCheck(
  model: ClassificationCardModel,
  field: ClassificationField,
): QuestionQualityCheck {
  if (field === "bloom") {
    const alternate = bloomAlternate(model.bloom)
    return {
      id: CLASSIFICATION_BLOOM_CHECK_ID,
      parameter: "cognitive",
      severity: "caution",
      field: "settings",
      settingsTarget: "bloom",
      title: "Bloom’s",
      mechanism: model.bloomReason,
      actionKind: "set-bloom",
      actionLabel: "Apply",
      patch: alternate.patch,
      alternates: [alternate],
    }
  }
  if (field === "difficulty") {
    const alternate = difficultyAlternate(model.difficulty || "easy")
    return {
      id: CLASSIFICATION_DIFFICULTY_CHECK_ID,
      parameter: "difficulty",
      severity: "caution",
      field: "settings",
      settingsTarget: "difficulty",
      title: "Difficulty",
      mechanism: model.difficultyReason,
      actionKind: "set-difficulty",
      actionLabel: "Apply",
      patch: alternate.patch,
      alternates: [alternate],
    }
  }
  const alternate = {
    id: "classification-category-apply",
    text: model.categoryLabel,
    actionKind: "set-classification" as const,
    actionLabel: "Apply",
    patch: {
      categoryIds: model.categoryId ? [model.categoryId] : [],
    },
    parameterDeltas: [{ parameter: "cognitive" as const, effect: "clears" as const }],
    clearsCheckIds: [
      CLASSIFICATION_CATEGORY_CHECK_ID,
      CLASSIFICATION_IDENTIFIED_CHECK_ID,
    ],
  }
  return {
    id: CLASSIFICATION_CATEGORY_CHECK_ID,
    parameter: "cognitive",
    severity: "caution",
    field: "settings",
    settingsTarget: "classification",
    title: "Category",
    mechanism: model.categoryReason,
    actionKind: "set-classification",
    actionLabel: "Apply",
    patch: alternate.patch,
    alternates: [alternate],
  }
}

export function classificationApplyAllCheck(
  model: ClassificationCardModel,
): QuestionQualityCheck {
  const alternate = {
    id: "classification-apply-all",
    text: [
      model.bloom,
      model.categoryLabel,
      model.difficulty ? DIFFICULTY_TAG_LABEL[model.difficulty] : "",
    ]
      .filter(Boolean)
      .join(", "),
    actionKind: "set-classification" as const,
    actionLabel: "Apply",
    patch: {
      ...(model.bloom ? { bloom: model.bloom } : {}),
      ...(model.difficulty ? { difficulty: model.difficulty } : {}),
      categoryIds: model.categoryId ? [model.categoryId] : [],
    },
    parameterDeltas: [
      { parameter: "cognitive" as const, effect: "clears" as const },
      { parameter: "difficulty" as const, effect: "clears" as const },
    ],
    clearsCheckIds: [
      CLASSIFICATION_BLOOM_CHECK_ID,
      CLASSIFICATION_DIFFICULTY_CHECK_ID,
      CLASSIFICATION_CATEGORY_CHECK_ID,
      CLASSIFICATION_IDENTIFIED_CHECK_ID,
    ],
  }
  return {
    id: CLASSIFICATION_IDENTIFIED_CHECK_ID,
    parameter: "cognitive",
    severity: "caution",
    field: "settings",
    settingsTarget: "classification",
    title: "Classification",
    mechanism: model.followUp,
    actionKind: "set-classification",
    actionLabel: "Apply",
    patch: alternate.patch,
    alternates: [alternate],
  }
}

export function classificationCardModel(
  snapshot: QuestionQualitySnapshot,
  ask?: ClassificationAsk | null,
  leo?: LeoClassificationPayload | null,
  categories: ExamCategory[] = DEFAULT_EXAM_CATEGORIES,
): ClassificationCardModel | null {
  const bloomSet = Boolean(normalizeBloomTag(snapshot.bloom))
  const difficultySet = Boolean(normalizeDifficultyTag(snapshot.difficulty))
  const categorySet = (snapshot.categoryIds?.length ?? 0) > 0
  if (
    bloomSet &&
    difficultySet &&
    categorySet &&
    !ask &&
    !leo
  ) {
    return null
  }

  const bloom =
    ask?.bloomLevel ||
    leo?.bloom ||
    normalizeBloomTag(snapshot.bloom) ||
    ""
  const difficulty =
    ask?.difficultyLevel ||
    (leo?.difficulty
      ? normalizeDifficultyTag(leo.difficulty)
      : undefined) ||
    normalizeDifficultyTag(snapshot.difficulty) ||
    ""
  const categoryId =
    leo?.categoryId || snapshot.categoryIds?.[0] || ""
  const categoryLabel = categoryLabelFor(categoryId, categories)
  const bloomReason = classificationReason(
    leo?.bloomReason,
    bloom
      ? "Confirm this Bloom's, or pick another."
      : "Pick the Bloom's level that matches what the student must do.",
  )
  const categoryReason = classificationReason(
    leo?.categoryReason,
    categoryId
      ? "Confirm this category, or pick another."
      : "Pick the category that matches this stem.",
  )
  const difficultyReason = classificationReason(
    leo?.difficultyReason,
    difficulty
      ? "Confirm this difficulty, or pick another."
      : "Pick Low, Normal, or High for this stem.",
  )

  return {
    bloom,
    categoryId,
    categoryLabel,
    difficulty,
    bloomReason,
    categoryReason,
    categoryCandidates: (leo?.categoryId
      ? [
          { id: leo.categoryId, reason: leo.categoryCandidates?.find((candidate) => candidate.id === leo.categoryId)?.reason ?? leo.categoryReason ?? "This is the main skill tested by the question." },
          ...(leo.categoryCandidates ?? []).filter((candidate) => candidate.id !== leo.categoryId),
        ]
      : leo?.categoryCandidates ?? []).map((candidate) => ({
      id: candidate.id,
      path: getCategoryPathLabel(categories, candidate.id),
      reason: classificationReason(candidate.reason, "Review whether this is the main knowledge or skill tested."),
    })),
    difficultyReason,
    followUp: classificationFollowUp({
      bloom,
      categoryLabel,
      difficulty,
    }),
  }
}
